import { newUi } from "./featureFlags";

/**
 * D109 (PR #846, second independent review) — "when the new screen itself
 * says what just happened, no success toast fires on arrival there."
 * Concretely: "You're all set!" (Onboarding.jsx/OnboardingNew.jsx) and
 * "Account created!" (Register.jsx/SignUpNew.jsx) must not survive onto
 * the new empty record, because that screen already says "Nothing on it
 * yet" — but every OTHER destination, and the flag-off leg, keep exactly
 * the toasts they have on `main`.
 *
 * THE FIRST FIX WAS WRONG. `RecordEmptyScreen.jsx` used to call
 * `toast.dismiss()` (plus staggered follow-ups) on its own mount, on the
 * theory that "the screen itself is the confirmation" for whatever just
 * navigated there. But B3 (same PR, first review round) mounts THAT
 * component — as `loading` — for EVERY flag-on `/dashboard` visit, a
 * POPULATED player's included, not only the empty-record arrival D109
 * actually names. A populated player signing in got "Welcome back!"
 * created and immediately dismissed, because nothing told the dismiss
 * call "only if this mount turns out to be the empty record" — it
 * couldn't, `loading` is the state that exists precisely because that
 * isn't known yet. Suppressing at the SOURCE — the page that is about to
 * fire a toast it is also about to navigate away from — sidesteps the
 * whole question: that page already knows its own destination, and
 * `newUi("record")` is a synchronous, already-known flag read, not
 * something that has to wait for a fetch.
 *
 * THE CHECK. `newUi("record")` on, and the navigation target's path
 * (stripped of any `?query`/`#hash`) is `/dashboard` — the one route
 * `RecordEmptyScreen` can ever replace. This is a deliberate proxy, not a
 * fetch of "will this specific account actually be empty": a player who
 * has just finished (or skipped) registration, by construction, cannot
 * yet have logged a match, so `newUi("record") && target === "/dashboard"`
 * and "this player's next `/dashboard` visit renders the empty record"
 * are the same fact for every caller of this function today. A signed-in
 * visitor already mid-session who happens to land back on `/dashboard`
 * (e.g. `Login.jsx`'s own "Welcome back!") is NOT a caller of this
 * function — sign-in never fires "You're all set!"/"Account created!",
 * so there was never a toast to suppress there, and that page's own
 * "Welcome back!" is untouched (see journey 11's own new check).
 *
 * ONE function, not four copies. `Onboarding.jsx`, `OnboardingNew.jsx`,
 * `Register.jsx` and `SignUpNew.jsx` each call this instead of re-deriving
 * the same two-part condition — a change to it that updated three of the
 * four call sites would silently leave the fourth showing (or hiding) a
 * toast the others don't, the exact failure mode `lib/homePath.js`'s own
 * header warns against for "where a signed-in account belongs."
 */
export function arrivalToastSuppressed(target) {
  const path = String(target || "").split(/[?#]/)[0];
  return newUi("record") && path === "/dashboard";
}
